import { has, isEnd, mustEnd, optional, parse as parseWith, ParseGenerator, ParseYieldable } from 'yieldparser';

const whitespaceOptional = /^\s*/;

function* ParseInt() {
    const [stringValue]: [string] = yield /^\d+/;
    return parseInt(stringValue, 10);
}

const repeatsChoices = Object.freeze(['day', 'week', 'month', 'year'] as const);
type Repeat = (typeof repeatsChoices)[0 | 1 | 2 | 3];

const repeatingTypeChoices = Object.freeze(['daily', 'weekly', 'monthly', 'yearly'] as const);
type RepeatType = (typeof repeatingTypeChoices)[0 | 1 | 2 | 3];

const monthChoices = Object.freeze([
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
] as const);
export type Month = (typeof monthChoices)[0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11];

function* EveryParser() {
    yield /^every\b/;
    yield whitespaceOptional;

    let month: Month | undefined = yield optional(...monthChoices);

    yield whitespaceOptional;

    let interval = yield optional(ParseInt);
    const ext = yield optional('th', 'nd', 'rd');

    yield whitespaceOptional;

    const repeats: Repeat = yield optional(...repeatsChoices);

    if (repeats && interval) yield optional(/^s/);

    let monthDay: number | undefined = undefined;
    if (ext || !repeats) {
        monthDay = interval;
        interval = undefined;
        if (!month) {
            month = yield optional(...monthChoices);
        }
    }

    return { monthDay, month, interval, repeats };
}

const weekdayChoices = Object.freeze(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const);
export type Weekday = (typeof weekdayChoices)[0 | 1 | 2 | 3 | 4 | 5 | 6];

const weekdayAliases: Record<Weekday, readonly string[]> = {
    monday: ['понедельник', 'понедельника', 'понедельнику', 'понедельником', 'понедельники', 'pondělí'],
    tuesday: ['вторник', 'вторника', 'вторнику', 'вторником', 'вторники', 'úterý'],
    wednesday: ['среда', 'среды', 'среду', 'среде', 'средой', 'средам', 'středa', 'středy', 'středu'],
    thursday: ['четверг', 'четверга', 'четвергу', 'четвергом', 'четверги', 'čtvrtek', 'čtvrtku', 'čtvrtky'],
    friday: ['пятница', 'пятницы', 'пятницу', 'пятнице', 'пятницей', 'пятницам', 'pátek', 'pátku', 'pátky'],
    saturday: ['суббота', 'субботы', 'субботу', 'субботе', 'субботой', 'субботам', 'sobota', 'soboty', 'sobotu'],
    sunday: ['воскресенье', 'воскресенья', 'воскресенью', 'воскресеньем', 'neděle', 'neděli'],
};

const workdayWeekdays: readonly Weekday[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

function normalizeRepeatInput(input: string): string {
    let normalized = input.toLocaleLowerCase().replace(/ё/gu, 'е').trim();

    for (const [weekday, aliases] of Object.entries(weekdayAliases) as [Weekday, readonly string[]][]) {
        for (const alias of aliases) {
            normalized = normalized.replace(new RegExp(alias, 'gu'), weekday);
        }
    }

    normalized = normalized
        .replace(/по\s+будням/gu, 'every work day')
        .replace(/кажд(?:ый|ую|ые)\s+рабоч(?:ий|ие)\s+д(?:ень|ня|ней)/gu, 'every work day')
        .replace(/každ(?:ý|é)\s+pracovní\s+d(?:en|ny)/gu, 'every work day')
        .replace(/кроме/gu, 'except')
        .replace(/kromě/gu, 'except')
        .replace(/ежедневно/gu, 'daily')
        .replace(/еженедельно/gu, 'weekly')
        .replace(/ежемесячно/gu, 'monthly')
        .replace(/ежегодно/gu, 'yearly')
        .replace(/denně/gu, 'daily')
        .replace(/týdně/gu, 'weekly')
        .replace(/měsíčně/gu, 'monthly')
        .replace(/ročně/gu, 'yearly')
        .replace(/кажд(?:ый|ую|ые)\s+(\d+)\s+д(?:ень|ня|ней)/gu, 'every $1 days')
        .replace(/кажд(?:ый|ую|ые)\s+д(?:ень|ня|ней)/gu, 'every day')
        .replace(/кажд(?:ый|ую|ые)\s+(\d+)\s+недел(?:ю|и|ь)/gu, 'every $1 weeks')
        .replace(/кажд(?:ый|ую|ые)\s+недел(?:ю|и|ь)/gu, 'every week')
        .replace(/кажд(?:ый|ую|ые)\s+(\d+)\s+месяц(?:а|ев)?/gu, 'every $1 months')
        .replace(/кажд(?:ый|ую|ые)\s+месяц(?:а|ев)?/gu, 'every month')
        .replace(/кажд(?:ый|ую|ые)\s+(\d+)\s+(?:год(?:а|ов)?|лет)/gu, 'every $1 years')
        .replace(/кажд(?:ый|ую|ые)\s+(?:год(?:а|ов)?|лет)/gu, 'every year')
        .replace(/každ(?:ý|é)\s+(\d+)\s+d(?:en|ny|ní)/gu, 'every $1 days')
        .replace(/každ(?:ý|é)\s+d(?:en|ny|ní)/gu, 'every day')
        .replace(/každ(?:ý|é)\s+(\d+)\s+týd(?:en|ny|nů)/gu, 'every $1 weeks')
        .replace(/každ(?:ý|é)\s+týd(?:en|ny|nů)/gu, 'every week')
        .replace(/každ(?:ý|é)\s+(\d+)\s+měsíc(?:e|ů)?/gu, 'every $1 months')
        .replace(/každ(?:ý|é)\s+měsíc(?:e|ů)?/gu, 'every month')
        .replace(/každ(?:ý|é)\s+(\d+)\s+(?:rok(?:y|ů)?|let)/gu, 'every $1 years')
        .replace(/každ(?:ý|é)\s+(?:rok(?:y|ů)?|let)/gu, 'every year')
        .replace(/každ(?:ý|ую|ые)\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/gu, 'every $1')
        .replace(/každ(?:ý|é)\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/gu, 'every $1')
        .replace(/по\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/gu, 'every $1')
        .replace(/в\s+(\d{1,2}(?::\d{1,2})?)/gu, 'at $1')
        .replace(/v\s+(\d{1,2}(?::\d{1,2})?)/gu, 'at $1')
        .replace(/с\s+(\d{1,2}(?::\d{1,2})?)\s+до\s+(\d{1,2}(?::\d{1,2})?)/gu, 'from $1 to $2')
        .replace(/od\s+(\d{1,2}(?::\d{1,2})?)\s+do\s+(\d{1,2}(?::\d{1,2})?)/gu, 'from $1 to $2');

    const localizedWeekdayMatch = normalized.match(
        /(?:каждый|каждую|каждые|každý|každé)\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/u,
    );
    if (localizedWeekdayMatch) {
        normalized = normalized.replace(localizedWeekdayMatch[0], `every ${localizedWeekdayMatch[1]}`);
    }

    const workdayMatch = normalized.match(
        /\bevery\s+(?:work\s*days?|weekdays?)(?:\s+except\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday))?/u,
    );
    if (workdayMatch) {
        const excludedWeekday = workdayMatch[1] as Weekday | undefined;
        const weekdays = workdayWeekdays.filter(weekday => weekday !== excludedWeekday);
        normalized = normalized.replace(workdayMatch[0], `every ${weekdays.join(' ')}`);
    }

    return normalized;
}

function* WeekdayParser() {
    let repeats: boolean = yield has(/^every\b/);
    yield optional(/^next\b/);

    yield whitespaceOptional;
    yield optional(/^on\b/);
    yield whitespaceOptional;

    const weekday: Weekday = yield weekdayChoices;
    repeats = repeats || (yield has(/^[s]\b/));

    return { weekday, repeats };
}

function* AnotherWeekdayParser() {
    yield whitespaceOptional;
    yield optional('and', 'or');
    yield whitespaceOptional;
    return yield WeekdayParser;
}

function* WeekdaysParser() {
    let repeats = false;

    const weekdays = new Set<Weekday>();

    let result: { weekday: Weekday; repeats: boolean };
    result = yield WeekdayParser;

    if (result.weekday) weekdays.add(result.weekday);
    repeats = repeats || result.repeats;

    // tslint:disable-next-line:no-conditional-assignment
    while ((result = yield optional(AnotherWeekdayParser))) {
        weekdays.add(result.weekday);
        repeats = repeats || result.repeats;
    }

    return { weekdays, repeats };
}

function* MinutesSuffixParser() {
    yield ':';
    const minutes = yield ParseInt;
    return minutes;
}

function* TimeOfDayParser() {
    let hours = yield ParseInt;
    const minutes = yield optional(MinutesSuffixParser);
    const amOrPm = yield optional('am', 'pm');
    if (amOrPm === 'pm' && hours <= 11) {
        hours += 12;
    } else if (amOrPm === 'am' && hours === 12) {
        hours = 24;
    }
    if (hours > 24 || hours < 0 || minutes >= 60 || minutes < 0) return undefined;
    return { hours, minutes };
}

function* TimespanSuffixParser() {
    const started = yield optional('to', '-', '–', '—', 'until');
    if (started === undefined) return undefined;
    yield whitespaceOptional;
    return yield TimeOfDayParser;
}

function* TimespanParser() {
    yield ['from', 'at', ''];
    yield whitespaceOptional;
    const startTime = yield TimeOfDayParser;
    yield whitespaceOptional;
    const endTime = yield optional(TimespanSuffixParser);
    return { startTime, endTime };
}

export interface RepeatParsingResult {
    weekdays: Set<Weekday>;
    interval: number | undefined;
    monthDay: number | undefined;
    month: Month | undefined;
    repeats: RepeatType | undefined;
    startTime: { hours: number; minutes?: number } | undefined;
    endTime: { hours: number; minutes?: number } | undefined;
}

function mapRepeat(repeat: Repeat): RepeatType | undefined {
    switch (repeat) {
        case 'day':
            return 'daily';
        case 'week':
            return 'weekly';
        case 'month':
            return 'monthly';
        case 'year':
            return 'yearly';
        default:
            return undefined;
    }
}

function* RepeatDateParser() {
    let repeats: RepeatType | undefined = yield optional(...repeatingTypeChoices);
    let interval: number | undefined = undefined;
    let monthDay: number | undefined = undefined;
    let month: string | undefined = undefined;
    let hasEvery: boolean = false;

    if (!repeats) {
        const every: any = yield optional(EveryParser);
        repeats = mapRepeat(every?.repeats);
        interval = every?.interval;
        monthDay = every?.monthDay;
        month = every?.month;
        hasEvery = every !== undefined;
    }

    let weekSpan: any;

    if (!repeats || repeats === 'weekly') {
        yield whitespaceOptional;
        weekSpan = yield optional(WeekdaysParser);
        yield whitespaceOptional;

        if (weekSpan) repeats = repeats || (weekSpan.repeats || hasEvery ? 'weekly' : undefined);
    }

    const weekdays = repeats === 'weekly' ? weekSpan?.weekdays || new Set([]) : weekSpan?.weekdays;

    if (monthDay) repeats = month ? 'yearly' : 'monthly';

    return { repeats, interval, monthDay, month, weekdays };
}

function* NaturalDateParser(): ParseGenerator<RepeatParsingResult> {
    const repeatData: any = yield RepeatDateParser;

    yield whitespaceOptional;
    const timespan: any = yield optional(TimespanParser);
    yield whitespaceOptional;

    return {
        ...repeatData,
        ...timespan,
    };
}

export function parse(input: string): RepeatParsingResult | null {
    input = normalizeRepeatInput(input);
    input = input.replace(/[,]/g, '');
    const parsedResult = parseWith(input, NaturalDateParser());
    return parsedResult.success ? parsedResult.result : null;
}
